<template>
  <div ref="modalRef" class="modal" data-bs-backdrop="static" tabindex="-1" v-on:[shownEvent]="givenNameInputRef?.focus()">
    <div class="modal-dialog modal-dialog-centered">
      <form class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title">
            Welcome!
          </h1>
          <div>Let's confirm a few things.</div>
        </div>
        <div class="modal-body">
          <section class="name-section">
            <img src="@/assets/icons/person_gray_150.png" class="user-icon"/>
            <h4>Preferred name</h4>
            <div class="help-text">
              This helps other people identify you as a user on the site.
              It should be your current preferred name.
            </div>
            <div class="name-editor">
              <div>
                <label class="form-label">Given name</label>
                <input type="text" ref="givenNameInputRef" class="form-control" :disabled="busyRef" v-model="editGivenNameRef"/>
              </div>
              <div>
                <label class="form-label">Surname</label>
                <input type="text" class="form-control" :disabled="busyRef" v-model="editSurnameRef"/>
              </div>
            </div>
          </section>
          <section class="location-section">
            <MapPinIcon size="1.8x"/>
            <h4>Your location</h4>
            <div class="help-text">
              This can help distinguish you from other people with the same name.
              Just the city or state you live in is fine.
            </div>
            <div class="profile-name">
              <input type="text" class="form-control" :disabled="busyRef" v-model="editLocationRef" placeholder="Anytown, ST"/>
            </div>
            <div v-if="locationGuessedRef" class="help-text">
              We took a guess based on your device's internet address.
              Just correct it if it's wrong.
            </div>
          </section>
        </div>
        <div class="modal-footer">
          <button type="submit" class="btn btn-primary" :disabled="!enableSaveRef" @click.prevent="save">
            <Spinner v-if="busyRef"/>
            Confirm
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-header {
  display: flex;
  flex-direction: column;
  align-items: center;

}

.modal-body {
  padding: 0.5rem 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

section {
  position: relative;
  padding-left: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & > img, & > svg {
    position: absolute;
    margin-left: -2.5rem;
  }

  h4 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: normal;
    color: #888;
  }
}

.help-text {
  font-size: 0.75rem;
}

img.user-icon {
  height: 30px;
  border-radius: 50%;
  background-color: #ddd;
  padding: 4px;
  opacity: 60%;
}

svg.feather-map-pin {
  stroke: #888;
  fill: #888;

  :deep(circle) {
    stroke: white;
  }
}

.name-editor {
  display: flex;
  gap: 1rem;
}

.alert-warning {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 1rem;
  align-items: center;

  img {
    width: 24px;
  }
}

.modal-footer {
  button {
    min-width: 100px;
  }
}
</style>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useUserStore } from '@/gp/UserStore'
import { Modal as BsModal } from 'bootstrap'
import { PatchChange } from '@/util/Api'
import { MapPinIcon } from '@zhuowenli/vue-feather-icons'
import Spinner from '@/util/Spinner.vue'
import { AsyncUtil } from '@/util/AsyncUtil'

defineExpose({
  open
})

const shownEvent = 'shown.bs.modal'

const busyRef = ref(false)
const editGivenNameRef = ref('')
const editSurnameRef = ref('')
const editLocationRef = ref('')
const locationGuessedRef = ref(false)

const userStore = useUserStore()

const userRef = computed(() => userStore.getAsyncSelf()?.data) // loaded by app
const areChangesValidRef = computed(() => (!!editGivenNameRef.value.trim() || !!editSurnameRef.value.trim()))
const enableSaveRef = computed(() => !busyRef.value && areChangesValidRef.value)

const modalRef = ref<HTMLElement>()
const givenNameInputRef = ref<HTMLInputElement>()

function open() {
  editGivenNameRef.value = userRef.value?.givenName ?? ''
  editSurnameRef.value = userRef.value?.surname ?? ''
  editLocationRef.value = userRef.value?.metadata?.location ?? ''
  locationGuessedRef.value = !!editLocationRef.value

  // HACK: Not sure why getInstance isn't sufficient here, but sometimes it returns null because apparently the BS Modal
  // hasn't been initialized yet. Calling getOrCreateInstance seems to fix it.
  BsModal.getOrCreateInstance(modalRef.value!).show()
}

async function save() {
  if (!userRef.value)
    throw new Error('User not loaded')

  try {
    busyRef.value = true

    const changes = [
      new PatchChange('/metadata/nameConfirmed', 'true')
    ]

    if (editGivenNameRef.value != (userRef.value?.givenName ?? '')) {
      changes.push(new PatchChange('/givenName', editGivenNameRef.value))
    }
    if (editSurnameRef.value != (userRef.value?.surname ?? '')) {
      changes.push(new PatchChange('/surname', editSurnameRef.value))
    }
    if (editLocationRef.value != (userRef.value?.metadata?.location ?? '')) {
      changes.push(new PatchChange('/metadata/location', editLocationRef.value))
    }

    await AsyncUtil.atLeast(500, userStore.patchAsync(userRef.value.id!, changes))

    BsModal.getInstance(modalRef.value!)?.hide()
  }
  finally {
    busyRef.value = false
  }
}
</script>
